import { dataService } from '../services/dataService.js';
import { createMovieCard } from '../components/MovieCard.js';
import { seoService } from '../services/seoService.js';

export async function renderHomeView() {
  seoService.update({
    title: 'Discover Sri Lankan Cinema',
    description: 'Explore trending Sinhala films, soundtrack lyrics, cast timelines, and filming locations on CinemaHala.'
  });

  const container = document.createElement('div');
  container.className = 'main-content';

  const index = await dataService.getIndex();
  if (!index) {
    container.innerHTML = `<p style="text-align: center; color: var(--text-muted); padding: var(--space-2xl) 0;">Failed to load catalog.</p>`;
    return container;
  }

  // Find featured movie
  const movies = index.searchRegistry.filter(i => i.type === 'movie');
  const featured = movies[0] || {};
  const people = index.searchRegistry.filter(i => i.type === 'person');

  container.innerHTML = `
    <!-- Hero Banner -->
    <div class="hero-banner">
      <img src="${featured.poster}" alt="${featured.title}" class="hero-backdrop" />
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <span class="hero-badge">★ Spotlight Release</span>
        <h1 class="hero-title">${featured.title}</h1>
        <div class="hero-sinhala">${featured.sinhalaTitle || ''}</div>
        <p class="hero-desc">
          Directed by ${featured.director} (${featured.year}). Rated ${featured.rating} / 5 by local cinephiles.
        </p>
        <div class="hero-actions">
          <a href="#/movie/${featured.id}" class="btn-primary clickable-item">
            Explore Movie
          </a>
          <a href="#/songs" class="btn-secondary clickable-item">
            Listen Songs
          </a>
        </div>
      </div>
    </div>

    <!-- Trending Movies Section -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header">
        <h2 class="section-title">Trending in Theaters</h2>
        <a href="#/watchlist" class="see-all-link">My Saved</a>
      </div>
      <div class="horizontal-scroll" id="trending-movies-row"></div>
    </section>

    <!-- Popular Soundtrack Stream Section -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header">
        <h2 class="section-title">Soundtrack & Sinhala Lyrics</h2>
        <a href="#/songs" class="see-all-link">Browse All</a>
      </div>
      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--space-sm);">
        <a href="#/song/gal-lena-bindala" class="song-card clickable-item">
          <div class="song-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle>
            </svg>
          </div>
          <div class="song-info">
            <h4 class="song-title">Gal Lena Bindala</h4>
            <div style="font-size: 0.78rem; color: var(--accent-gold);">ගල් ලෙන බිඳලා • Sinhabahu</div>
            <span class="song-artists">W.D. Amaradeva, Nanda Malini</span>
          </div>
          <span class="badge-pill badge-gold" style="font-size: 0.7rem;">Lyrics</span>
        </a>

        <a href="#/song/sandakada-pahana" class="song-card clickable-item">
          <div class="song-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle>
            </svg>
          </div>
          <div class="song-info">
            <h4 class="song-title">Sandakada Pahana</h4>
            <div style="font-size: 0.78rem; color: var(--accent-gold);">සඳකඩ පහණ • Nidhanaya</div>
            <span class="song-artists">Sunil Edirisinghe</span>
          </div>
          <span class="badge-pill badge-gold" style="font-size: 0.7rem;">Lyrics</span>
        </a>
      </div>
    </section>

    <!-- Celebrated Stars & Cast Section -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header">
        <h2 class="section-title">Celebrated Stars & Directors</h2>
      </div>
      <div class="horizontal-scroll" id="featured-people-row"></div>
    </section>

    <!-- Scenic Filming Locations Section -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header">
        <h2 class="section-title">Filming Locations & Scenic Spots</h2>
        <a href="#/locations" class="see-all-link">Explore Map</a>
      </div>
      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--space-md);">
        <a href="#/location/ella-demodara" class="song-card clickable-item" style="padding: 14px;">
          <img src="https://images.unsplash.com/photo-1546708973-b339540b5162?auto=format&fit=crop&w=300&q=80" alt="Ella" style="width: 72px; height: 72px; border-radius: var(--radius-sm); object-fit: cover;" />
          <div class="song-info">
            <h4 class="song-title">Ella & Demodara</h4>
            <div style="font-size: 0.78rem; color: var(--accent-gold);">ඇල්ල සහ දෙමෝදර</div>
            <span class="song-artists">Featured in Sinhabahu (2024)</span>
          </div>
        </a>

        <a href="#/location/galle-fort" class="song-card clickable-item" style="padding: 14px;">
          <img src="https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=300&q=80" alt="Galle Fort" style="width: 72px; height: 72px; border-radius: var(--radius-sm); object-fit: cover;" />
          <div class="song-info">
            <h4 class="song-title">Galle Fort Ramparts</h4>
            <div style="font-size: 0.78rem; color: var(--accent-gold);">ගාල්ල කොටුව</div>
            <span class="song-artists">Featured in Nidhanaya & Kandak Sema</span>
          </div>
        </a>
      </div>
    </section>
  `;

  // Populate Movies
  const movieRow = container.querySelector('#trending-movies-row');
  movies.forEach(m => movieRow.appendChild(createMovieCard(m)));

  // Populate People
  const peopleRow = container.querySelector('#featured-people-row');
  people.forEach(p => {
    const card = document.createElement('a');
    card.href = `#/person/${p.id}`;
    card.className = 'person-card clickable-item';
    card.innerHTML = `
      <div class="person-avatar-wrap">
        <img src="${p.avatar}" alt="${p.title}" class="person-avatar" loading="lazy" />
      </div>
      <div class="person-name">${p.title}</div>
      <div style="font-size: 0.72rem; color: var(--accent-gold);">${p.sinhalaTitle || ''}</div>
      <div class="person-role">${p.role || ''}</div>
    `;
    peopleRow.appendChild(card);
  });

  return container;
}
